/**
 * Shared chart component: same look & feel as PC PortalPage chart.
 * Used by PortalPage and MobilePortalPage. Supports mouse and touch.
 */
import React, { useRef, useCallback, useId } from "react";

function buildMonotonePath(points, getX, getY) {
  const n = points.length;
  if (n === 0) return "";
  const xs = points.map(getX);
  const ys = points.map(getY);
  let d = new Array(n - 1);
  for (let i = 0; i < n - 1; i++) d[i] = (ys[i + 1] - ys[i]) / (xs[i + 1] - xs[i] || 1);
  let m = new Array(n);
  m[0] = d[0];
  m[n - 1] = d[n - 2];
  for (let i = 1; i < n - 1; i++) {
    if (d[i - 1] * d[i] <= 0) m[i] = 0;
    else m[i] = (d[i - 1] + d[i]) / 2;
  }
  for (let i = 0; i < n - 1; i++) {
    if (d[i] === 0) {
      m[i] = 0;
      m[i + 1] = 0;
      continue;
    }
    const a = m[i] / d[i];
    const b = m[i + 1] / d[i];
    const s = a * a + b * b;
    if (s > 9) {
      const t = 3 / Math.sqrt(s);
      m[i] = t * a * d[i];
      m[i + 1] = t * b * d[i];
    }
  }
  let path = `M${xs[0]},${ys[0]}`;
  for (let i = 0; i < n - 1; i++) {
    const h = xs[i + 1] - xs[i];
    path += ` C ${xs[i] + h / 3},${ys[i] + (m[i] * h) / 3} ${xs[i + 1] - h / 3},${ys[i + 1] - (m[i + 1] * h) / 3} ${xs[i + 1]},${ys[i + 1]}`;
  }
  return path;
}

const PortalChart = React.memo(function PortalChart({
  data,
  hoverX,
  setHoverX,
  hoverYear,
  setHoverYear,
  enableInvestment,
  payoffByInvestment,
  payoffMonths,
  enableDelay,
  delayYears,
  delayMonths,
  totalTermYears,
  totalTermMonths = 0,
  planYears,
  chartHeight = 400,
  fontSize = 12,
  showPayoffLabelInChart = true,
  showInvestmentPayoffLabel = false,
  lineStrokeWidth = 2,
}) {
  const svgRef = useRef(null);
  const gradientId = useId().replace(/:/g, "-");
  const fs = fontSize;
  const stroke = lineStrokeWidth;
  // Scale padding with font size so large text (e.g. mobile fs=35) isn't clipped
  // Use equal left/right padding so the plot is centered (same margin each side)
  const padLeft = Math.round(72 + (fs - 12) * 4);
  const padding = {
    top: Math.round(24 + (fs - 12) * 1.8),
    right: padLeft,
    bottom: Math.round(36 + (fs - 12) * 2.5),
    left: padLeft,
  };
  const width = 900;
  const height = chartHeight;
  const xLabelOffset = Math.round(18 * (fs / 12));
  const tooltipH = Math.round(22 * (fs / 12));
  const tooltipW = Math.round(52 * (fs / 12));
  const tooltipR = Math.round(26 * (fs / 12));

  const maxY = Math.max(...data.flatMap((d) => [d.principal, d.interest, d.investment || 0]), 1);
  const maxX =
    enableDelay && (delayYears > 0 || delayMonths > 0)
      ? totalTermYears + (totalTermMonths > 0 ? totalTermMonths / 12 : 0)
      : planYears;

  const x = (yr) => padding.left + (yr / maxX) * (width - padding.left - padding.right);
  const y = (v) => height - padding.bottom - (v / maxY) * (height - padding.top - padding.bottom);

  const repaymentPoint =
    enableInvestment && payoffByInvestment && data.length > 1
      ? (() => {
          for (let i = 0; i < data.length - 1; i++) {
            const current = data[i];
            const next = data[i + 1];
            const investmentBefore = current.investment || 0;
            const investmentAfter = next.investment || 0;
            const principalBefore = current.principal || 0;
            const principalAfter = next.principal || 0;
            if (
              investmentBefore < principalBefore &&
              investmentAfter >= principalAfter &&
              principalAfter > 0
            ) {
              const invDiff = investmentAfter - investmentBefore;
              const princDiff = principalAfter - principalBefore;
              const diff = invDiff - princDiff;
              if (Math.abs(diff) > 1e-9) {
                const t = (principalBefore - investmentBefore) / diff;
                if (t >= 0 && t <= 1) {
                  const exactYear = current.year + t * (next.year - current.year);
                  const exactPrincipal = principalBefore + t * princDiff;
                  const exactInvestment = investmentBefore + t * invDiff;
                  return {
                    year: exactYear,
                    principal: exactPrincipal,
                    investment: exactInvestment,
                    interest: current.interest + t * (next.interest - current.interest),
                  };
                }
              }
            }
            if (Math.abs(investmentBefore - principalBefore) < 1e-6 && principalBefore > 0) {
              return current;
            }
          }
          const payoffYear = payoffMonths / 12;
          let closestPoint = null;
          let minDistance = Infinity;
          for (const d of data) {
            const distance = Math.abs(d.year - payoffYear);
            if (distance < minDistance) {
              minDistance = distance;
              closestPoint = d;
            }
          }
          return closestPoint || data[data.length - 1];
        })()
      : null;

  const principalPath =
    data.length > 1
      ? (() => {
          if (repaymentPoint) {
            const pointsBeforeRepayment = [];
            for (const d of data) {
              if (d.year < repaymentPoint.year) pointsBeforeRepayment.push(d);
              else break;
            }
            if (pointsBeforeRepayment.length === 0) {
              return `M${x(repaymentPoint.year)},${y(repaymentPoint.principal)}`;
            }
            let path = `M${x(pointsBeforeRepayment[0].year)},${y(pointsBeforeRepayment[0].principal)}`;
            for (let i = 1; i < pointsBeforeRepayment.length; i++) {
              path += `L${x(pointsBeforeRepayment[i].year)},${y(pointsBeforeRepayment[i].principal)}`;
            }
            path += `L${x(repaymentPoint.year)},${y(repaymentPoint.principal)}`;
            return path;
          }
          return (
            `M${x(data[0].year)},${y(data[0].principal)}` +
            data.slice(1).map((d) => `L${x(d.year)},${y(d.principal)}`).join("")
          );
        })()
      : "";
  const interestPath = buildMonotonePath(data, (d) => x(d.year), (d) => y(d.interest));
  const investmentPath =
    enableInvestment && data.length > 1
      ? (() => {
          if (repaymentPoint) {
            const pointsBeforeRepayment = [];
            for (const d of data) {
              if (d.year < repaymentPoint.year) pointsBeforeRepayment.push(d);
              else break;
            }
            if (pointsBeforeRepayment.length === 0) {
              return `M${x(repaymentPoint.year)},${y(repaymentPoint.investment || 0)}`;
            }
            let path = `M${x(pointsBeforeRepayment[0].year)},${y(pointsBeforeRepayment[0].investment || 0)}`;
            for (let i = 1; i < pointsBeforeRepayment.length; i++) {
              path += `L${x(pointsBeforeRepayment[i].year)},${y(pointsBeforeRepayment[i].investment || 0)}`;
            }
            path += `L${x(repaymentPoint.year)},${y(repaymentPoint.investment || 0)}`;
            return path;
          }
          return (
            `M${x(data[0].year)},${y(data[0].investment || 0)}` +
            data.slice(1).map((d) => `L${x(d.year)},${y(d.investment || 0)}`).join("")
          );
        })()
      : "";

  const areaPath = (() => {
    if (repaymentPoint && principalPath) {
      return `${principalPath} L ${x(repaymentPoint.year)},${y(0)} L ${x(data[0].year)},${y(0)} Z`;
    }
    return `${principalPath} L ${x(data[data.length - 1].year)},${y(0)} L ${x(data[0].year)},${y(0)} Z`;
  })();

  const yTicks = (() => {
    if (maxY <= 0) return [];
    const step = maxY / 3;
    const intervals = [];
    for (let i = 1; i <= 3; i++) intervals.push(Math.round(step * i));
    return intervals;
  })();

  const formatYLabel = (val) => {
    const r = Math.round(val);
    if (r >= 1000) return `€${Math.round(r / 1000)}k`;
    return `€${r}`;
  };

  const xTicks = [];
  for (let year = 0; year <= maxX; year += 5) xTicks.push(year);

  const payoffYear = payoffMonths / 12;
  const maxYearToShow = payoffYear || maxX;

  const nearest = (() => {
    const year = hoverYear != null ? hoverYear : (hoverX != null && svgRef.current
      ? (() => {
          let svgX = hoverX;
          const svgRect = svgRef.current.getBoundingClientRect();
          if (svgRect.width > 0) svgX = hoverX * (width / svgRect.width);
          return Math.max(0, Math.min(maxYearToShow, ((svgX - padding.left) / (width - padding.left - padding.right)) * maxX));
        })()
      : null);
    if (year == null) return null;
    const dataToShow = payoffYear ? data.filter((d) => d.year <= payoffYear) : data;
    let best = null;
    let bestDist = Infinity;
    for (const d of dataToShow) {
      const dx = Math.abs(d.year - year);
      if (dx < bestDist) {
        best = d;
        bestDist = dx;
      }
    }
    return best ? { ...best, year: Math.min(year, maxYearToShow) } : null;
  })();

  const handlePointerMove = useCallback(
    (e) => {
      const clientX = e.touches?.[0]?.clientX ?? e.changedTouches?.[0]?.clientX ?? e.clientX;
      const target = svgRef.current;
      if (!target) return;
      const rect = target.getBoundingClientRect();
      if (setHoverYear) {
        const localX = clientX - rect.left;
        const svgX = rect.width > 0 ? localX * (width / rect.width) : localX;
        const year = Math.max(0, Math.min(maxYearToShow, ((svgX - padding.left) / (width - padding.left - padding.right)) * maxX));
        setHoverYear(year);
      } else if (setHoverX) {
        setHoverX(clientX - rect.left);
      }
    },
    [setHoverYear, setHoverX, maxYearToShow, padding, width]
  );
  const handlePointerLeave = useCallback(() => {
    if (setHoverYear) setHoverYear(null);
    else if (setHoverX) setHoverX(null);
  }, [setHoverYear, setHoverX]);

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMinYMid meet"
        style={{ width: "100%", height: "auto" }}
        onMouseMove={handlePointerMove}
        onMouseLeave={handlePointerLeave}
        onTouchMove={handlePointerMove}
        onTouchEnd={handlePointerMove}
        onTouchCancel={handlePointerLeave}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3399FF" stopOpacity="0.22" />
            <stop offset="100%" stopColor="#3399FF" stopOpacity="0.05" />
          </linearGradient>
        </defs>
        <rect x={0} y={0} width={width} height={height} fill="#ffffff" rx={12} />
        <line
          x1={padding.left}
          x2={padding.left}
          y1={padding.top}
          y2={height - padding.bottom}
          stroke="#CBD5E1"
        />
        <line
          x1={padding.left}
          x2={width - padding.right}
          y1={height - padding.bottom}
          y2={height - padding.bottom}
          stroke="#CBD5E1"
        />
        {yTicks.map((t, i) => (
          <line
            key={`gy-${i}`}
            x1={padding.left}
            x2={width - padding.right}
            y1={y(t)}
            y2={y(t)}
            stroke="#f3f4f6"
            strokeWidth={1}
          />
        ))}
        <text x={padding.left} y={Math.max(14, fs + 2)} fill="#374151" fontSize={fs}>
          Euro
        </text>
        <text x={width - padding.right} y={height - 4} fill="#374151" fontSize={fs} textAnchor="end">
          Jaren
        </text>
        {yTicks.map((t, i) => (
          <text
            key={`ty-${i}`}
            x={padding.left - 8}
            y={y(t)}
            textAnchor="end"
            dominantBaseline="middle"
            fill="#6b7280"
            fontSize={fs}
          >
            {formatYLabel(t)}
          </text>
        ))}
        {xTicks.map((t, i) => (
          <text
            key={`tx-${i}`}
            x={x(t)}
            y={height - padding.bottom + xLabelOffset}
            textAnchor="middle"
            fill="#6b7280"
            fontSize={fs}
          >
            {t}
          </text>
        ))}
        <path d={areaPath} fill={`url(#${gradientId})`} stroke="none" />
        <path d={principalPath} fill="none" stroke="#3b5bfd" strokeWidth={stroke} strokeLinecap="round" />
        <path d={interestPath} fill="none" stroke="#94a3b8" strokeWidth={stroke} strokeLinecap="round" />
        {enableInvestment && investmentPath && (
          <path d={investmentPath} fill="none" stroke="#10b981" strokeWidth={stroke} strokeLinecap="round" />
        )}
        {showPayoffLabelInChart && (showInvestmentPayoffLabel || (enableInvestment && payoffByInvestment)) && data.length > 0 && (() => {
          const payoffPoint = data[data.length - 1];
          const payoffX = x(payoffPoint.year);
          const payoffY = y(payoffPoint.principal);
          const textX = Math.min(payoffX + 20, width - padding.right - 10);
          const textY = Math.max(payoffY - 10, padding.top + 20);
          return (
            <text x={textX} y={textY} textAnchor="start" fill="#3b5bfd" fontSize={fs} fontWeight="500">
              Lening afbetaald via investering
            </text>
          );
        })()}
        {nearest && (
          <g>
            <line
              x1={x(nearest.year)}
              x2={x(nearest.year)}
              y1={padding.top}
              y2={height - padding.bottom}
              stroke="#D1D5DB"
            />
            <circle
              cx={x(nearest.year)}
              cy={y(nearest.principal)}
              r={5}
              fill="#fff"
              stroke="#3b5bfd"
              strokeWidth={stroke}
            />
            <circle
              cx={x(nearest.year)}
              cy={y(nearest.interest)}
              r={5}
              fill="#fff"
              stroke="#94a3b8"
              strokeWidth={stroke}
            />
            {enableInvestment && nearest.investment !== undefined && (
              <circle
                cx={x(nearest.year)}
                cy={y(nearest.investment)}
                r={5}
                fill="#fff"
                stroke="#10b981"
                strokeWidth={stroke}
              />
            )}
            {nearest.year % 5 === 0 && (
              <text
                x={x(nearest.year)}
                y={height - padding.bottom + xLabelOffset}
                textAnchor="middle"
                fill="#6b7280"
                fontSize={fs}
              >
                {Math.round(nearest.year)}
              </text>
            )}
            <g>
              {(() => {
                const tooltipY = Math.max(padding.top + 5, y(nearest.principal) - (tooltipH + 8));
                return (
                  <>
                    <rect
                      x={x(nearest.year) - tooltipR}
                      y={tooltipY}
                      rx={8}
                      ry={8}
                      width={tooltipW}
                      height={tooltipH}
                      fill="#3b5bfd"
                    />
                    <text
                      x={x(nearest.year)}
                      y={tooltipY + tooltipH / 2 + fs / 2}
                      textAnchor="middle"
                      fill="#fff"
                      fontSize={fs}
                      fontWeight="600"
                    >
                      {Math.round(nearest.principal)}
                    </text>
                  </>
                );
              })()}
            </g>
            <g>
              {(() => {
                const tooltipY = Math.max(padding.top + 5, y(nearest.interest) - (tooltipH + 8));
                return (
                  <>
                    <rect
                      x={x(nearest.year) - tooltipR}
                      y={tooltipY}
                      rx={8}
                      ry={8}
                      width={tooltipW}
                      height={tooltipH}
                      fill="#94a3b8"
                    />
                    <text
                      x={x(nearest.year)}
                      y={tooltipY + tooltipH / 2 + fs / 2}
                      textAnchor="middle"
                      fill="#fff"
                      fontSize={fs}
                      fontWeight="600"
                    >
                      {Math.round(nearest.interest)}
                    </text>
                  </>
                );
              })()}
            </g>
            {enableInvestment && nearest.investment !== undefined && (
              <g>
                {(() => {
                  const tooltipY = Math.max(padding.top + 5, y(nearest.investment) - (tooltipH + 8));
                  return (
                    <>
                      <rect
                        x={x(nearest.year) - tooltipR}
                        y={tooltipY}
                        rx={8}
                        ry={8}
                        width={tooltipW}
                        height={tooltipH}
                        fill="#10b981"
                      />
                      <text
                        x={x(nearest.year)}
                        y={tooltipY + tooltipH / 2 + fs / 2}
                        textAnchor="middle"
                        fill="#fff"
                        fontSize={fs}
                        fontWeight="600"
                      >
                        {Math.round(nearest.investment)}
                      </text>
                    </>
                  );
                })()}
              </g>
            )}
          </g>
        )}
      </svg>
    </div>
  );
});

export default PortalChart;
